import { emptyThemeData, normalizeThemeData, type ThemeDecoration } from "./themeTypes.js";

export type ShopUnlockType = "dots" | "free" | "achievement" | "challenge" | "score";

export type CosmeticCategory =
  | "hat"
  | "face"
  | "cursor"
  | "cursor_color"
  | "trail"
  | "banner"
  | "theme";

export type TrailMode = "multiColor" | "particle";

/** Player-owned Theme cosmetic channels (independent of Admin Game Theme). */
export type ThemeCosmeticChannelKey = "background" | "balls" | "buttons" | "text";

export type ThemeCosmeticFillMode = "solid" | "gradient";

export type ThemeCosmeticChannel = {
  mode: ThemeCosmeticFillMode;
  /** 1 hex for solid; 1–10 for gradient. */
  colors: string[];
  /** Gradient angle in degrees (0–359). Ignored for solid. */
  angleDeg?: number;
};

/** Only configured channels are present on a Theme item. */
export type ThemeCosmeticData = Partial<Record<ThemeCosmeticChannelKey, ThemeCosmeticChannel>> & {
  /** Particle systems (Theme Creator–compatible; particles kind only). */
  decorations?: ThemeDecoration[];
};

export const THEME_COSMETIC_CHANNEL_KEYS: ThemeCosmeticChannelKey[] = [
  "background",
  "balls",
  "buttons",
  "text",
];

export const THEME_COSMETIC_CHANNEL_LABELS: Record<ThemeCosmeticChannelKey, string> = {
  background: "Background",
  balls: "Balls",
  buttons: "Buttons",
  text: "Text",
};

/** Equip slots for Theme cosmetics (colour channels + optional Particles). */
export type ThemeCosmeticEquipSlot = ThemeCosmeticChannelKey | "particles";

export const THEME_COSMETIC_EQUIP_SLOT_KEYS: ThemeCosmeticEquipSlot[] = [
  ...THEME_COSMETIC_CHANNEL_KEYS,
  "particles",
];

export const THEME_COSMETIC_EQUIP_SLOT_LABELS: Record<ThemeCosmeticEquipSlot, string> = {
  ...THEME_COSMETIC_CHANNEL_LABELS,
  particles: "Particles",
};

export const COSMETIC_CATEGORIES: CosmeticCategory[] = [
  "hat",
  "face",
  "cursor",
  "cursor_color",
  "trail",
  "banner",
  "theme",
];

export const COSMETIC_CATEGORY_LABELS: Record<CosmeticCategory, string> = {
  hat: "Hat",
  face: "Face",
  cursor: "Body",
  cursor_color: "Cursor",
  trail: "Trails",
  banner: "Banners",
  theme: "Themes",
};

/** Banner art canvas (Shop Creator). */
export const BANNER_ART_W = 256;
export const BANNER_ART_H = 64;

export const MAX_TRAIL_COLORS = 10;
export const MAX_THEME_GRADIENT_COLORS = 10;

/** Public asset URLs for a cosmetic (no base64). */
export type ShopCosmeticAssets = {
  image: string;
  outline: string | null;
  matchMask: string | null;
  frames: string[];
  matchMaskFrames: string[];
};

export type ShopCosmeticDto = {
  id: string;
  name: string;
  /**
   * Art source: data URL / base64 (legacy embed) OR `/api/cosmetic-asset?…` URL (lite catalog).
   * Client `loadCosmeticImage` accepts both.
   */
  imageData: string;
  outlineMaskData: string | null;
  /** Animated overlay frames (data URLs or asset URLs); static art remains in imageData. */
  animationFrames?: string[] | null;
  category: CosmeticCategory;
  allowCustomColor: boolean;
  allowRepurchase: boolean;
  /** Non-outline fill follows player cursor color at runtime. */
  matchCursorColor?: boolean;
  /** -1 darker … 0 exact cursor … 1 lighter (legacy flat; prefer matchCursorMaskData). */
  cursorColorShade?: number;
  /** PNG mask: A>0 follow cursor; R = (shade+1)*127.5. */
  matchCursorMaskData?: string | null;
  /** Per-frame match masks parallel to animationFrames. */
  matchCursorMaskFrames?: string[] | null;
  /** Trail cosmetics only: multiColor | particle. */
  trailMode?: TrailMode | null;
  /** Ordered colors for multiColor trails (1–10 hex). */
  trailColors?: string[] | null;
  /** Theme cosmetics: per-channel solid/gradient fills (only configured keys). */
  themeData?: ThemeCosmeticData | null;
  unlockType: ShopUnlockType;
  priceDots: number | null;
  unlockAchievementId: string | null;
  unlockChallengeId: string | null;
  unlockScoreMode: string | null;
  unlockScoreValue: number | null;
  sortOrder: number;
  /** Hidden from Shop → Items; only via crates. */
  crateOnly?: boolean;
  /** Hidden from Shop; granted only via admin Send to. */
  sentOnly?: boolean;
  /** Hidden from Shop; granted only via Campaign node completion. */
  campaignOnly?: boolean;
  /** Campaign node id this cosmetic is tied to (when campaignOnly / Campaign reward). */
  unlockCampaignNodeId?: string | null;
  /** Inventory Index: mode + goal for the Campaign node that grants this cosmetic. */
  campaignUnlockLabel?: string | null;
  /** Hidden from Shop; granted only via Task claim. */
  taskOnly?: boolean;
  /** TaskDefinition id this cosmetic is tied to (when taskOnly / Task reward). */
  unlockTaskId?: string | null;
  /** Hidden from Shop; granted only via Marathon first-reach reward. */
  marathonOnly?: boolean;
  /** MarathonSegment id this cosmetic is tied to (when marathonOnly). */
  unlockMarathonSegmentId?: string | null;
  /** Hidden from Shop; granted only via Battle Pass claim. */
  battlePassOnly?: boolean;
  /** BattlePassLevel id this cosmetic is tied to (when battlePassOnly). */
  unlockBattlePassLevelId?: string | null;
  /** Timed Shop listing. */
  limitedItem?: boolean;
  /** Admin list: how many users currently own this (purchases). */
  recipientCount?: number;
  /** Publisher duration text (e.g. "2d 15h"). */
  limitedDurationText?: string | null;
  /** ISO time when the limited sale ends. */
  limitedEndsAt?: string | null;
  /** When the cosmetic was created / published to Shop. */
  createdAt?: string | null;
  /** Hide filled play cursor; draw art only (hitbox unchanged). Category locked to Cursor. */
  newCursor?: boolean;
  /** Present on lite catalog responses. */
  assets?: ShopCosmeticAssets;
  unlockLabel?: string;
  /** @deprecated prefer ownedCustomColors / activeCustomColor */
  customColor?: string | null;
  /** Signed-in owner's purchased tints for this cosmetic. */
  ownedCustomColors?: string[];
  /** Tint used when this cosmetic is equipped. */
  activeCustomColor?: string | null;
};

export type ShopCrateContentDto = {
  cosmeticId: string;
  cosmeticName: string;
  cosmeticCategory: CosmeticCategory;
  cosmeticImageData: string;
  cosmeticOutlineMaskData: string | null;
  cosmeticAnimationFrames?: string[] | null;
  cosmeticMatchCursorColor?: boolean;
  cosmeticCursorColorShade?: number;
  cosmeticMatchCursorMaskData?: string | null;
  cosmeticMatchCursorMaskFrames?: string[] | null;
  cosmeticTrailMode?: TrailMode | null;
  cosmeticTrailColors?: string[] | null;
  cosmeticThemeData?: ThemeCosmeticData | null;
  /** Hide filled play cursor behind New Cursor cosmetics in previews. */
  cosmeticNewCursor?: boolean;
  probabilityPercent: number;
};

export type ShopCrateDto = {
  id: string;
  name: string;
  imageData: string;
  unlockType: ShopUnlockType;
  priceDots: number | null;
  unlockAchievementId: string | null;
  unlockChallengeId: string | null;
  unlockScoreMode: string | null;
  unlockScoreValue: number | null;
  sortOrder: number;
  unlockLabel?: string;
  contents: ShopCrateContentDto[];
  claimed?: boolean;
};

export const MAX_COSMETIC_ANIMATION_FRAMES = 10;
export const MAX_COSMETIC_IMAGE_CHARS = 400_000;

/** Normalize animation frame list from JSON / client payload (max 10). */
export function normalizeAnimationFrames(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null;
  const out: string[] = [];
  for (const v of raw) {
    if (typeof v !== "string") continue;
    const s = v.trim();
    if (!s || s.length > MAX_COSMETIC_IMAGE_CHARS) continue;
    out.push(s);
    if (out.length >= MAX_COSMETIC_ANIMATION_FRAMES) break;
  }
  return out.length > 0 ? out : null;
}

export type ModeDotsConfigDto = {
  modeId: string;
  dotsConversionRate: number;
  dotsBonusBase: number;
};

export const DEFAULT_MODE_DOTS_CONFIG: ModeDotsConfigDto[] = [
  "normal", "wasd", "superdot", "progression", "roulette", "sandbox", "highway",
  "darkness", "mirror", "snake", "combo", "overload", "colorrush", "heat",
  "hunter", "pop", "counter", "aim", "hook", "tilt", "orbit", "delay", "penalty",
].map((modeId) => ({
  modeId,
  dotsConversionRate: 100,
  dotsBonusBase: 50,
}));

export function parseUnlockType(raw: unknown): ShopUnlockType | null {
  const s = String(raw || "").trim();
  if (s === "dots" || s === "free" || s === "achievement" || s === "challenge" || s === "score") return s;
  return null;
}

export function parseCosmeticCategory(raw: unknown): CosmeticCategory {
  const s = String(raw || "").trim().toLowerCase().replace(/\s+/g, "_");
  if (
    s === "hat" ||
    s === "face" ||
    s === "cursor_color" ||
    s === "trail" ||
    s === "banner" ||
    s === "theme"
  ) {
    return s;
  }
  // cursor_body / body alias the body-fill slot (stored as "cursor").
  if (s === "cursor" || s === "cursor_body" || s === "body") return "cursor";
  return "cursor";
}

export function parseTrailMode(raw: unknown): TrailMode | null {
  const s = String(raw || "").trim();
  if (s === "multiColor" || s === "particle") return s;
  return null;
}

/** Normalize ordered trail color list (1–10 unique-or-repeat hex colors). */
export function normalizeTrailColors(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null;
  const out: string[] = [];
  for (const v of raw) {
    const c = parseCustomColorHex(v);
    if (!c) continue;
    out.push(c);
    if (out.length >= MAX_TRAIL_COLORS) break;
  }
  return out.length > 0 ? out : null;
}

function normalizeThemeChannel(raw: unknown): ThemeCosmeticChannel | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const mode = o.mode === "gradient" ? "gradient" : o.mode === "solid" ? "solid" : null;
  if (!mode) return null;
  const colors: string[] = [];
  if (Array.isArray(o.colors)) {
    for (const v of o.colors) {
      const c = parseCustomColorHex(v);
      if (!c) continue;
      colors.push(c);
      if (colors.length >= MAX_THEME_GRADIENT_COLORS) break;
    }
  }
  if (mode === "solid") {
    if (!colors.length) {
      const single = parseCustomColorHex(o.color ?? o.hex);
      if (single) colors.push(single);
    }
    if (!colors.length) return null;
    return { mode: "solid", colors: [colors[0]] };
  }
  if (colors.length < 1) return null;
  let angleDeg = Math.round(Number(o.angleDeg ?? o.angle ?? 180));
  if (!Number.isFinite(angleDeg)) angleDeg = 180;
  angleDeg = ((angleDeg % 360) + 360) % 360;
  return { mode: "gradient", colors, angleDeg };
}

/** Normalize Theme cosmetic payload; drops empty / invalid channels. */
export function normalizeThemeCosmeticData(raw: unknown): ThemeCosmeticData | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const out: ThemeCosmeticData = {};
  for (const key of THEME_COSMETIC_CHANNEL_KEYS) {
    const ch = normalizeThemeChannel(o[key]);
    if (ch) out[key] = ch;
  }
  if (Array.isArray(o.decorations) && o.decorations.length) {
    const normalized = normalizeThemeData({
      ...emptyThemeData(),
      decorations: o.decorations,
    });
    const particles = normalized.decorations.filter((d) => d.kind === "particles");
    if (particles.length) out.decorations = particles;
  }
  return Object.keys(out).length > 0 ? out : null;
}

export function themeCosmeticConfiguredKeys(data: ThemeCosmeticData | null | undefined): ThemeCosmeticChannelKey[] {
  if (!data) return [];
  return THEME_COSMETIC_CHANNEL_KEYS.filter((k) => !!data[k]);
}

export function themeHasParticles(data: ThemeCosmeticData | null | undefined): boolean {
  return !!data?.decorations?.some((d) => d.kind === "particles");
}

/** Colour channels plus Particles when this Theme defines particle decorations. */
export function themeCosmeticEquipSlots(
  data: ThemeCosmeticData | null | undefined,
): ThemeCosmeticEquipSlot[] {
  const slots: ThemeCosmeticEquipSlot[] = [...themeCosmeticConfiguredKeys(data)];
  if (themeHasParticles(data)) slots.push("particles");
  return slots;
}

/** Exclusive equip slots: Cursor (cursor_color), Trail, and Banner. Themes use per-channel equip. */
export function isExclusiveCosmeticCategory(cat: string | null | undefined): boolean {
  return cat === "cursor_color" || cat === "trail" || cat === "banner";
}

/**
 * Cursor section (cursor_color): always rematch live fill in Color Rush / Heat / etc.
 * Body (cursor) rematches only when Settings "Cosmetics match cursor" is on — like Hat/Face.
 */
export function isCursorLiveTintCategory(cat: string | null | undefined): boolean {
  return cat === "cursor_color";
}

/** Modes that force / replace the play cursor fill color. */
export function modeForcesCursorColor(mode: string | null | undefined): boolean {
  return mode === "colorrush" || mode === "heat";
}

export function parseCustomColorHex(raw: unknown): string | null {
  const s = String(raw || "").trim();
  const m = /^#?([0-9a-fA-F]{6})$/.exec(s);
  if (!m) return null;
  return `#${m[1].toLowerCase()}`;
}

/** Shade offset for match-cursor cosmetics: -1 darker … 0 exact … 1 lighter. */
export function parseCursorColorShade(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(-1, Math.min(1, n));
}

export function normalizeCustomColorList(raw: unknown): string[] {
  const out: string[] = [];
  if (Array.isArray(raw)) {
    for (const v of raw) {
      const c = parseCustomColorHex(v);
      if (c && !out.includes(c)) out.push(c);
    }
  } else {
    const single = parseCustomColorHex(raw);
    if (single) out.push(single);
  }
  return out;
}

/** @deprecated Cursor cosmetics no longer hide — kept for older imports. */
export function modeHidesCursorColorCosmetics(mode: string | null | undefined): boolean {
  return modeForcesCursorColor(mode);
}

const UNLOCK_SCORE_MODE_LABELS: Record<string, string> = {
  normal: "Normal",
  wasd: "WASD",
  superdot: "Superdot",
  progression: "Progression",
  roulette: "Roulette",
  sandbox: "Sandbox",
  speedrun: "SpeedRun",
  highway: "Highway",
  darkness: "Darkness",
  mirror: "Mirror",
  snake: "Snake",
  combo: "Combo",
  overload: "Overload",
  colorrush: "Color Rush",
  heat: "Heat",
  hunter: "Hunter",
  counter: "Counter",
  aim: "AIM",
  hook: "Hook",
  tilt: "Tilt",
  orbit: "Orbit",
  delay: "Delay",
  penalty: "Golf",
};

export function formatScoreModeLabel(mode: string | null | undefined): string {
  const id = String(mode || "").trim();
  if (!id) return "Unknown mode";
  return UNLOCK_SCORE_MODE_LABELS[id] || id;
}

export function formatUnlockLabel(
  row: Pick<
    ShopCosmeticDto,
    | "unlockType"
    | "priceDots"
    | "unlockAchievementId"
    | "unlockChallengeId"
    | "unlockScoreMode"
    | "unlockScoreValue"
  >,
  opts?: { achievementTitle?: string | null },
): string {
  switch (row.unlockType) {
    case "dots":
      return row.priceDots != null ? `${row.priceDots} Dots` : "—";
    case "free":
      return "Free";
    case "achievement": {
      const title = String(opts?.achievementTitle || "").trim();
      return title ? `Achievement: ${title}` : "Complete an achievement";
    }
    case "challenge":
      return row.unlockChallengeId ? `Challenge: ${row.unlockChallengeId}` : "Complete a challenge";
    case "score":
      if (row.unlockScoreMode && row.unlockScoreValue != null) {
        const modeLabel = formatScoreModeLabel(row.unlockScoreMode);
        const n = Number(row.unlockScoreValue).toLocaleString();
        if (row.unlockScoreMode === "progression") {
          return `Level ${n} in ${modeLabel}`;
        }
        return `Score ${n} in ${modeLabel}`;
      }
      return "Score required";
    default:
      return "—";
  }
}
